import {
  Timestamp,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

const PLAN_PRICES = { free: 0, starter: 499, growth: 999, pro: 1999 };

export function businessRef(batchDb, businessId) {
  return doc(batchDb, 'businesses', businessId);
}

export function featureFlagsRef(batchDb) {
  return doc(batchDb, 'config', 'featureFlags');
}

export function gstMasterVersionRef(batchDb) {
  return doc(batchDb, 'config', 'gstMasterVersion');
}

export function promoRef(batchDb, code) {
  return doc(batchDb, 'promoCodes', String(code || '').toUpperCase());
}

export function adminRef(batchDb, email) {
  return doc(batchDb, 'admins', String(email || '').toLowerCase());
}

export function adminTaskRef(batchDb, taskId) {
  return doc(batchDb, 'adminTasks', taskId);
}

export async function fetchAdminByEmail(email) {
  const ref = doc(db, 'admins', String(email || '').toLowerCase());
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function fetchFeatureFlags() {
  const snap = await getDoc(doc(db, 'config', 'featureFlags'));
  return snap.exists() ? snap.data() : {
    enablePhoneAuth: false,
    enableStorefront: false,
    enableBarcode: false,
    enableLoyaltyProgram: false,
    maintenanceMode: false,
    maintenanceMessage: '',
  };
}

export async function fetchGstMasterVersion() {
  const snap = await getDoc(doc(db, 'config', 'gstMasterVersion'));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function enrichGstKnowledgeBase(version) {
  const callable = httpsCallable(functions, 'enrichGstKnowledgeBase');
  const result = await callable({ version });
  return result.data || {};
}

export async function fetchOverviewStats() {
  const businessesSnap = await getDocs(collection(db, 'businesses'));
  const businesses = businessesSnap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));

  const totalMerchants = businesses.length;
  const activePaid = businesses.filter((biz) => biz.planStatus === 'active').length;
  const activeTrials = businesses.filter((biz) => biz.planStatus === 'trial').length;
  const estimatedMRR = businesses.reduce((sum, biz) => {
    if (biz.planStatus !== 'active') return sum;
    return sum + (PLAN_PRICES[String(biz.plan || 'free').toLowerCase()] || 0);
  }, 0);

  const planDistribution = businesses.reduce((acc, biz) => {
    const key = `${biz.plan || 'free'}:${biz.planStatus || 'unknown'}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const recentSignups = [...businesses].sort((a, b) => {
    const aTs = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
    const bTs = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
    return bTs - aTs;
  }).slice(0, 10);

  return {
    totalMerchants,
    activePaid,
    activeTrials,
    estimatedMRR,
    planDistribution,
    recentSignups,
  };
}

export async function fetchMerchants({ plan, status } = {}) {
  const snap = await getDocs(collection(db, 'businesses'));
  let docs = snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));

  if (plan) {
    docs = docs.filter((merchant) => String(merchant.plan || '').toLowerCase() === String(plan).toLowerCase());
  }

  if (status) {
    docs = docs.filter((merchant) => String(merchant.planStatus || '').toLowerCase() === String(status).toLowerCase());
  }

  docs.sort((a, b) => {
    const aTs = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
    const bTs = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
    return bTs - aTs;
  });

  return {
    merchants: docs,
    nextCursor: null,
  };
}

export async function fetchMerchantById(businessId) {
  const snap = await getDoc(doc(db, 'businesses', businessId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function fetchMerchantInvoices(businessId, rowLimit = 5) {
  const invoicesRef = collection(db, 'businesses', businessId, 'invoices');
  const invoicesQuery = query(invoicesRef, orderBy('createdAt', 'desc'), limit(rowLimit));
  const snap = await getDocs(invoicesQuery);
  return snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
}

export async function fetchPromos() {
  const snap = await getDocs(query(collection(db, 'promoCodes'), orderBy('createdAt', 'desc')));
  return snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
}

export async function checkPromoCodeExists(code) {
  const snap = await getDoc(doc(db, 'promoCodes', String(code || '').toUpperCase()));
  return snap.exists();
}

export async function fetchPromoUsages(code) {
  const usageRef = collection(db, 'promoCodes', code, 'usages');
  let usageSnap;

  try {
    usageSnap = await getDocs(query(usageRef, orderBy('appliedAt', 'desc'), limit(100)));
  } catch (error) {
    usageSnap = await getDocs(query(usageRef, limit(100)));
  }

  const usages = usageSnap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
  const businessIds = [...new Set(usages.map((usage) => usage.businessId).filter(Boolean))];
  const businessNameMap = {};

  await Promise.all(businessIds.map(async (businessId) => {
    const businessSnap = await getDoc(doc(db, 'businesses', businessId));
    if (!businessSnap.exists()) {
      businessNameMap[businessId] = '-';
      return;
    }
    const data = businessSnap.data();
    businessNameMap[businessId] = data.name || data.businessName || '-';
  }));

  return usages.map((usage) => ({
    ...usage,
    businessName: usage.businessId ? businessNameMap[usage.businessId] || '-' : '-',
  }));
}

export async function fetchAuditLogs({
  action = '',
  startDate = '',
  endDate = '',
} = {}) {
  const constraints = [orderBy('timestamp', 'desc')];

  if (action) {
    constraints.push(where('action', '==', action));
  }
  if (startDate) {
    const from = new Date(`${startDate}T00:00:00`);
    constraints.push(where('timestamp', '>=', Timestamp.fromDate(from)));
  }
  if (endDate) {
    const to = new Date(`${endDate}T23:59:59.999`);
    constraints.push(where('timestamp', '<=', Timestamp.fromDate(to)));
  }

  const logsQuery = query(collection(db, 'adminAuditLog'), ...constraints);
  const snap = await getDocs(logsQuery);
  return snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
}

export async function fetchAllBusinessesForAnalytics() {
  const snap = await getDocs(collection(db, 'businesses'));
  return snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
}

export async function fetchAdmins() {
  const snap = await getDocs(query(collection(db, 'admins'), orderBy('addedAt', 'desc')));
  return snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
}

export async function estimateBroadcastRecipients(filter) {
  let targetQuery = collection(db, 'businesses');
  if (filter === 'trial') {
    targetQuery = query(collection(db, 'businesses'), where('planStatus', '==', 'trial'));
  }
  if (filter === 'active') {
    targetQuery = query(collection(db, 'businesses'), where('planStatus', '==', 'active'));
  }

  const countSnap = await getCountFromServer(targetQuery);
  return countSnap.data().count;
}

export async function fetchAdminTasks({
  status = '',
  type = '',
  rowLimit = 50,
  cursor = null,
} = {}) {
  const constraints = [];
  if (status) constraints.push(where('status', '==', status));
  if (type) constraints.push(where('type', '==', type));
  constraints.push(orderBy('createdAt', 'desc'));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(rowLimit));

  const tasksQuery = query(collection(db, 'adminTasks'), ...constraints);
  const snap = await getDocs(tasksQuery);
  return {
    tasks: snap.docs.map((entry) => ({ id: entry.id, ...entry.data() })),
    nextCursor: snap.docs[snap.docs.length - 1] || null,
  };
}

